<template>
  <div class="p-6">
    <h1 class="text-2xl font-bold mb-4">Create New Ad Campaign</h1>

    <!-- Campaign Title -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-1">Campaign Name</label>
      <input
        v-model="campaign.name"
        type="text"
        placeholder="Campaign name"
        class="border rounded p-2 w-full"
      />
    </div>

    <!-- Description -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-1">Description</label>
      <textarea
        v-model="campaign.description"
        placeholder="Short campaign description"
        class="border rounded p-2 w-full"
        rows="3"
      ></textarea>
    </div>

    <!-- Date Range -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium mb-1">Start Date</label>
        <input v-model="campaign.startDate" type="date" class="border rounded p-2 w-full" />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">End Date</label>
        <input v-model="campaign.endDate" type="date" class="border rounded p-2 w-full" />
      </div>
    </div>

    <!-- Budget -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium mb-1">Total Budget (KES)</label>
        <input v-model="campaign.totalBudget" type="number" class="border rounded p-2 w-full" />
      </div>
      <!-- <div>
        <label class="block text-sm font-medium mb-1">User Group (optional)</label>
        <select v-model="campaign.groupId" class="border rounded p-2 w-full">
          <option value="">— None —</option>
          <option v-for="g in userGroups" :key="g.id" :value="g.id">{{ g.name }}</option>
        </select>
      </div> -->
    </div>

    <!-- Creatives -->
    <div class="mb-6">
      <h2 class="text-lg font-semibold mb-2">Creatives</h2>

      <button
        @click="addCreative"
        class="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 mb-3"
        :disabled="loading"
      >
        + Add Creative
      </button>

      <div
        v-for="(c, index) in campaign.creatives"
        :key="index"
        class="mt-4 p-4 border rounded bg-gray-50"
      >
        <div class="flex items-center justify-between">
          <label class="block text-sm font-medium mb-1">Creative #{{ index + 1 }}</label>
          <button @click="removeCreative(index)" class="text-red-600 text-sm">Remove</button>
        </div>

        <select v-model="c.type" class="border rounded p-2 w-full mb-2">
          <option value="image">Image Banner</option>
          <option value="video">Video Ad</option>
          <option value="html">HTML Ad Tag</option>
          <option value="html5">HTML5 (zip / creative)</option>
        </select>

        <!-- File input / preview -->
        <div v-if="c.type !== 'html'" class="mb-2">
          <input
            :ref="'file-' + index"
            type="file"
            :accept="c.type === 'image' ? 'image/*' : (c.type === 'video' ? 'video/*' : '*/*')"
            @change="onFileUpload($event, index)"
            :disabled="loading"
          />

          <div v-if="c.previewUrl" class="mt-3 flex items-start gap-3">
            <div v-if="c.type === 'image'">
              <img :src="c.previewUrl" alt="preview" class="w-36 h-24 object-cover rounded border" />
            </div>
            <div v-else-if="c.type === 'video'">
              <video :src="c.previewUrl" class="w-36 h-24 object-cover rounded border" controls />
            </div>
            <div class="flex-1">
              <div class="text-sm font-medium">{{ c.file ? c.file.name : 'Selected file' }}</div>
              <div v-if="c.dimensions" class="text-xs text-gray-600">Size: {{ c.dimensions.width }}×{{ c.dimensions.height }}</div>

              <div v-if="c.uploadProgress !== null" class="w-full bg-gray-200 h-2 rounded mt-2">
                <div :style="{ width: c.uploadProgress + '%' }" class="h-2 rounded bg-teal-600"></div>
              </div>
              <div v-if="c.uploadError" class="text-xs text-red-600 mt-1">{{ c.uploadError }}</div>
              <div v-if="c.uploadedFilename" class="text-xs text-green-700 mt-1">Uploaded: {{ c.uploadedFilename }}</div>
            </div>
          </div>
        </div>

        <!-- HTML creative -->
        <div v-if="c.type === 'html'" class="mb-2">
          <textarea v-model="c.htmlTag" placeholder="<script> ... </script>" class="border rounded p-2 w-full" rows="4"></textarea>
        </div>

        <input v-model="c.clickUrl" type="text" placeholder="Click-through URL (optional)" class="border rounded p-2 w-full mt-2" />

      </div>
    </div>

    <!-- Zones selection -->
    <!-- <div class="mb-6">
      <label class="block text-sm font-medium mb-1">Ad Placements / Zones</label>
      <select v-model="campaign.zones" class="border rounded p-2 w-full" multiple>
        <option v-for="z in zones" :key="z.id" :value="z.id">{{ z.name }}</option>
      </select>
      <p class="text-xs text-gray-500">Hold CTRL (Windows) or CMD (Mac) to select multiple.</p>
    </div> -->

    <!-- Save -->
    <div class="flex items-center gap-3">
      <button
        @click="saveCampaign"
        class="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        :disabled="loading"
      >
        <span v-if="!loading">Save Campaign</span>
        <span v-else>Creating... ⏳</span>
      </button>

      <button v-if="loading" @click="cancelAll" class="bg-gray-200 text-gray-800 px-4 py-2 rounded">
        Cancel
      </button>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CreateCampaign",
  data: function () {
    return {
      campaign: {
        name: "",
        description: "",
        startDate: "",
        endDate: "",
        groupId: "",
        totalBudget: 0,
        creatives: [],
        zones: []
      },
      userGroups: [],
      zones: [],
      loading: false,
      // used to cancel axios requests if user clicks cancel
      cancelTokens: [],
    };
  },
  mounted: function () {
    this.addCreative(); // start with one creative
    this.loadGroups();
    this.loadZones();
  },
  methods: {
    addCreative: function () {
      this.campaign.creatives.push({
        type: "image",
        file: null,
        previewUrl: null,
        uploadProgress: null,
        uploadError: null,
        uploadedFilename: null,
        htmlTag: "",
        clickUrl: "",
        dimensions: null,
      });
    },

    removeCreative: function (index) {
      // revoke object URL if any
      var c = this.campaign.creatives[index];
      if (c && c.previewUrl) {
        try { URL.revokeObjectURL(c.previewUrl); } catch (e) { /* noop */ }
      }
      this.campaign.creatives.splice(index, 1);
    },

    onFileUpload: function (event, index) {
      var file = event.target.files && event.target.files[0];
      if (!file) return;

      var c = this.campaign.creatives[index];

      // quick validation
      var maxSize = 10 * 1024 * 1024; // 10MB default
      if (file.size > maxSize) {
        this.$root.$emit("alert_error", ["File too large (max 10MB)."]);
        return;
      }

      // accept types
      if (c.type === "image" && file.type.indexOf("image/") !== 0) {
        this.$root.$emit("alert_error", ["Please select an image file."]);
        return;
      }
      if (c.type === "video" && file.type.indexOf("video/") !== 0) {
        this.$root.$emit("alert_error", ["Please select a video file."]);
        return;
      }

      c.file = file;
      c.uploadProgress = null;
      c.uploadError = null;
      c.uploadedFilename = null;

      // create preview
      try {
        c.previewUrl = URL.createObjectURL(file);
      } catch (e) {
        c.previewUrl = null;
      }

      // if image, read dimensions
      if (c.type === "image") {
        var img = new Image();
      //  var self = this;
        img.onload = function () {
          c.dimensions = { width: img.naturalWidth, height: img.naturalHeight };
        };
        img.onerror = function () {
          c.dimensions = null;
        };
        img.src = c.previewUrl;
      } else {
        c.dimensions = null;
      }
    },

    async loadGroups() {
      try {
        var res = await axios.get("https://api.expressdata.co.ke/api/v1/usergroups");
        if (res && res.data) {
          this.userGroups = res.data.data || res.data || [];
        }
      } catch (e) {
        // ignore silently
      }
    },

    async loadZones() {
      try {
        // authenticate like other ad endpoints
        var loginRes = await axios.post("https://ads.brandfi.co.ke/ads/adsuiapi/login.php", {
          username: "b.arudo@saritcentre.com",
          password: "b.arudo@saritcentre.com",
          client_id: "BrandFiApiUI",
          client_secret: "BrandFiApiUI",
          grant_type: "password"
        }, { headers: { "Content-Type": "application/json" } });

        var token = (loginRes && loginRes.data && loginRes.data.access_token) ? loginRes.data.access_token : null;
        var url = "https://ads.brandfi.co.ke/ads/adsuiapi/agency/publisher-zones.php?access_token=" + token;
        var res = await axios.post(url, { affiliateid: 0, page: 1, agencyid: "15" });
        if (res && res.data) {
          this.zones = res.data.data || res.data || [];
        }
      } catch (e) {
        // ignore
      }
    },

    // helper to authenticate and return access_token
    async authenticate() {
      var loginParams = {
        username: "b.arudo@saritcentre.com",
        password: "b.arudo@saritcentre.com",
        client_id: "BrandFiApiUI",
        client_secret: "BrandFiApiUI",
        grant_type: "password"
      };
      var res = await axios.post("https://ads.brandfi.co.ke/ads/adsuiapi/login.php", loginParams, { headers: { "Content-Type": "application/json" } });
      if (res && res.data && res.data.access_token) {
        return res.data.access_token;
      }
      return null;
    },

    // upload file to fileupload.php (multipart/form-data). returns uploaded filename string.
    async uploadFile(file, accessToken, onProgress) {
      var form = new FormData();
      // per your example the field name is "profile"
      form.append("profile", file, file.name);

      var source = axios.CancelToken.source();
      this.cancelTokens.push(source);

      var url = "https://ads.brandfi.co.ke/ads/adsuiapi/fileupload.php?access_token=" + accessToken;

      var res = await axios.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
        cancelToken: source.token,
        onUploadProgress: function (progressEvent) {
          try {
            if (progressEvent.lengthComputable && typeof onProgress === "function") {
              var percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percent);
            }
          } catch (e) { /* noop */ }
        }
      });

      // fileupload returns a string like "52461e7d5b5ab4f0c61423b956abfac0.png"
      if (res && res.data) {
        // if res.data is an object with data.filename
        if (typeof res.data === "string") return res.data;
        if (res.data.filename) return res.data.filename;
        if (res.data.data && res.data.data.filename) return res.data.data.filename;
        // fallback: try first field
        if (typeof res.data === "object") {
          var keys = Object.keys(res.data);
          if (keys.length > 0) return res.data[keys[0]];
        }
      }
      return null;
    },

    // create banner record in ad server
    async createBanner(payload, accessToken) {
      var url = "https://ads.brandfi.co.ke/ads/adsuiapi/advertiser/banner-create.php?access_token=" + accessToken;
      var res = await axios.post(url, payload, { headers: { "Content-Type": "application/json" } });
      return res && res.data ? res.data : null;
    },

    // main orchestrator
    async saveCampaign() {
      // basic validation
      if (!this.campaign.name || this.campaign.name.trim() === "") {
        this.$root.$emit("alert_error", ["Campaign name required"]);
        return;
      }
      if (this.campaign.creatives.length === 0) {
        this.$root.$emit("alert_error", ["Add at least one creative"]);
        return;
      }
      if (this.campaign.startDate && this.campaign.endDate) {
        var s = new Date(this.campaign.startDate);
        var e = new Date(this.campaign.endDate);
        if (s > e) {
          this.$root.$emit("alert_error", ["Invalid date range"]);
          return;
        }
      }

      this.loading = true;
      this.cancelTokens = [];

      try {
        // 1) authenticate
        var accessToken = await this.authenticate();
        if (!accessToken) throw new Error("Authentication failed");

        // 2) create campaign
        var campaignCreateUrl = "https://ads.brandfi.co.ke/ads/adsuiapi/advertiser/campaign-create.php?access_token=" + accessToken;
        var campaignPayload = {
          campaignname: this.campaign.name,
          revenue_type: 1,
          revenue: this.campaign.totalBudget || 0,
          user_id: "81",
          clientid: "28",
          userType: "MANAGER",
          // include optional fields if provided
          description: this.campaign.description || "",
          start_time: this.campaign.startDate || "",
          end_time: this.campaign.endDate || "",
          user_group_id: this.campaign.groupId || ""
        };

        var campaignRes = await axios.post(campaignCreateUrl, campaignPayload, { headers: { "Content-Type": "application/json" } });

        // try multiple places for campaign id
        var createdCampaignId = null;
        if (campaignRes && campaignRes.data) {
          if (campaignRes.data.campaignid) createdCampaignId = campaignRes.data.campaignid;
          else if (campaignRes.data.data && campaignRes.data.data.campaignid) createdCampaignId = campaignRes.data.data.campaignid;
          else if (campaignRes.data.id) createdCampaignId = campaignRes.data.id;
        }
        console.log(createdCampaignId);
        if (!createdCampaignId) {
          // if the API returned something unexpected, still try to continue but warn
          console.warn("campaign-create response:", campaignRes && campaignRes.data ? campaignRes.data : campaignRes);
          throw new Error("Could not determine created campaign id");
        }

        // 3) upload creatives sequentially and create banners
        var createdBanners = [];
        for (var i = 0; i < this.campaign.creatives.length; i++) {
          var c = this.campaign.creatives[i];

          // reset states
          c.uploadProgress = 0;
          c.uploadError = null;
          c.uploadedFilename = null;

          try {
            var uploadedFilename = null;

            if (c.type !== "html") {
              if (!c.file) throw new Error("No file selected for creative #" + (i + 1));

              // upload file
              uploadedFilename = await this.uploadFile(c.file, accessToken, function (percent) {
                c.uploadProgress = percent;
              });
              if (!uploadedFilename) {
                throw new Error("Upload failed (no filename returned)");
              }
              c.uploadedFilename = uploadedFilename;
            }

            // create banner payload (fields adapted to your API)
            var bannerPayload = {
              filename: uploadedFilename || "",
              tag: "",
              width: (c.dimensions && c.dimensions.width) ? String(c.dimensions.width) : "",
              height: (c.dimensions && c.dimensions.height) ? String(c.dimensions.height) : "",
              bannername: (this.campaign.name + " - creative " + (i + 1)),
              ad_type: "web",
              url: c.clickUrl || "",
              user_id: "81",
              campaignid: createdCampaignId,
              country: [],
              devices: [],
              martial: []
            };

            if (c.type === "html") {
              // for HTML creative send html content instead of filename
              bannerPayload.html = c.htmlTag || "";
              // Ensure bannername and campaignid present
              bannerPayload.bannername = (this.campaign.name + " - HTML creative " + (i + 1));
            }

            var bannerRes = await this.createBanner(bannerPayload, accessToken);
            createdBanners.push(bannerRes);

            // mark progress 100%
            c.uploadProgress = 100;
          } catch (innerErr) {
            c.uploadError = innerErr && innerErr.message ? innerErr.message : String(innerErr);
            console.error("Creative error:", innerErr);
            // continue with next creative
          }
        }

        // 4) optionally attach zones to campaign (if your API supports)
        // skipping here — you can call an endpoint to attach zones using createdCampaignId and this.campaign.zones
         this.$router.push({ name: "ad_campaigns" });
        this.$root.$emit("alert_success", ["Campaign created successfully"]);
        // navigate back to campaigns list if route exists
       
      //  try { this.$router.push({ name: "campaigns" }); } catch (e) { /* ignore */ }
      } catch (err) {
        console.error("Save campaign error:", err);
        var msg = (err && err.message) ? err.message : "Failed to create campaign";
        this.$root.$emit("alert_error", [msg]);
      } finally {
        this.loading = false;
        // clear cancel tokens
        this.cancelTokens = [];
      }
    },

    // allow user to cancel ongoing uploads/requests
    cancelAll: function () {
      try {
        for (var i = 0; i < this.cancelTokens.length; i++) {
          var t = this.cancelTokens[i];
          if (t && t.cancel) t.cancel("User cancelled");
        }
      } catch (e) { /* noop */ }
      this.cancelTokens = [];
      this.loading = false;
    }
  }
};
</script>

<style scoped>
/* minimal styling */
button[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
